import { MathUtils, PerspectiveCamera, Vector3 } from "three";
import { toMapLocal, WorldMapPose } from "./world-map-alignment";
import { GeoPosition, mapLocalToGeo } from "./world-map-tiles";

const { RAD2DEG, DEG2RAD, clamp } = MathUtils;

/** A camera in the terms of the ArcGIS `Camera` */
export type GeoCamera = {
  longitude: number;
  latitude: number;
  /** Elevation in meters */
  z: number;
  /** Degrees clockwise from north */
  heading: number;
  /** Degrees from looking straight down */
  tilt: number;
  /** Diagonal field of view in degrees */
  fov: number;
};

/**
 * @param camera The viewer camera
 * @param origin The project coordinates
 * @param pose The placement of the map in the scene
 * @param mapHeight The height of the map in the scene
 * @param groundElevation The terrain elevation at the project, which maps to the map height
 * @returns The camera in geographic terms, seeing the map like the viewer camera sees the scene
 */
export function toGeoCamera(
  camera: PerspectiveCamera,
  origin: GeoPosition,
  pose: WorldMapPose,
  mapHeight: number,
  groundElevation: number,
): GeoCamera {
  const { position } = camera;
  const direction = camera.getWorldDirection(new Vector3());
  const [east, z] = toMapLocal(pose, [position.x, position.z]);
  const [aheadEast, aheadZ] = toMapLocal(pose, [
    position.x + direction.x,
    position.z + direction.z,
  ]);
  const { lat, lng } = mapLocalToGeo(origin, east, -z);
  return {
    longitude: lng,
    latitude: lat,
    z: groundElevation + position.y - mapHeight,
    heading: Math.atan2(aheadEast - east, z - aheadZ) * RAD2DEG,
    tilt: Math.acos(clamp(-direction.y, -1, 1)) * RAD2DEG,
    fov:
      2 *
      Math.atan(
        Math.tan((camera.fov * DEG2RAD) / 2) * Math.hypot(1, camera.aspect),
      ) *
      RAD2DEG,
  };
}
